package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppDatePickerField
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraOrder
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraReason
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnit
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_action_cancel
import portalvv.shared.generated.resources.compras_action_close
import portalvv.shared.generated.resources.compras_action_delete
import portalvv.shared.generated.resources.compras_action_save
import portalvv.shared.generated.resources.compras_extra_add_title
import portalvv.shared.generated.resources.compras_extra_col_delivery_date
import portalvv.shared.generated.resources.compras_extra_col_description
import portalvv.shared.generated.resources.compras_extra_col_order_date
import portalvv.shared.generated.resources.compras_extra_col_reason
import portalvv.shared.generated.resources.compras_extra_col_unit
import portalvv.shared.generated.resources.compras_extra_delete_message
import portalvv.shared.generated.resources.compras_extra_delete_title
import portalvv.shared.generated.resources.compras_extra_details_title
import portalvv.shared.generated.resources.compras_extra_edit_title
import portalvv.shared.generated.resources.compras_extra_reason_none
import portalvv.shared.generated.resources.compras_extra_reason_placeholder
import portalvv.shared.generated.resources.compras_extra_reason_search
import portalvv.shared.generated.resources.compras_extra_units_none
import portalvv.shared.generated.resources.compras_extra_units_placeholder
import portalvv.shared.generated.resources.compras_extra_units_search

@Composable
fun PurchasesExtraOrderDetailsDialog(
    order: PurchasesExtraOrder,
    onDismiss: () -> Unit,
) {
    AppModalCard(title = stringResource(Res.string.compras_extra_details_title), onDismiss = onDismiss) {
        Column(
            modifier = Modifier.fillMaxWidth().widthIn(max = 520.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            ExtraDetailRow(stringResource(Res.string.compras_extra_col_order_date), formatExtraDate(order.orderDate))
            ExtraDetailRow(stringResource(Res.string.compras_extra_col_delivery_date), formatExtraDate(order.deliveryDate))
            ExtraDetailRow(stringResource(Res.string.compras_extra_col_unit), order.unitName ?: "-")
            ExtraDetailRow(stringResource(Res.string.compras_extra_col_reason), extraReasonLabel(order.reasonName))
            ExtraDetailRow(
                stringResource(Res.string.compras_extra_col_description),
                order.description.ifBlank { "—" },
            )
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                AppButton(
                    text = stringResource(Res.string.compras_action_close),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Outlined,
                )
            }
        }
    }
}

@Composable
fun PurchasesExtraOrderEditDialog(
    order: PurchasesExtraOrder,
    units: List<PurchasesExtraUnit>,
    reasons: List<PurchasesExtraReason>,
    isNew: Boolean = false,
    onDismiss: () -> Unit,
    onSave: (PurchasesExtraOrder) -> Unit,
) {
    val noneLabel = stringResource(Res.string.compras_extra_units_none)
    val unitPlaceholder = stringResource(Res.string.compras_extra_units_placeholder)
    val noneReason = stringResource(Res.string.compras_extra_reason_none)
    val availableReasons =
        remember(reasons, order, isNew) {
            val active = reasons.filter { it.active }
            if (isNew) {
                active
            } else {
                val current =
                    reasons.firstOrNull { it.id == order.reasonId }
                        ?: order.reasonId?.let { id ->
                            PurchasesExtraReason(
                                id = id,
                                name = order.reasonName.orEmpty(),
                                active = order.reasonActive,
                            )
                        }
                if (current != null && !current.active) active + current else active
            }
        }
    val reasonLabels = remember(availableReasons) { availableReasons.associate { it.id to it.name } }
    val reasonOptions = remember(reasonLabels, noneReason) { listOf(noneReason) + reasonLabels.values }
    val labels = remember(units) { units.labels() }
    val options = remember(labels, noneLabel) { listOf(noneLabel) + labels.values }
    var orderDate by remember(order) { mutableStateOf(parseExtraDateInput(order.orderDate)) }
    var deliveryDate by remember(order) { mutableStateOf(parseExtraDateInput(order.deliveryDate)) }
    var description by remember(order) { mutableStateOf(order.description) }
    var selectedUnitLabel by remember(order) {
        mutableStateOf(order.unitId?.let(labels::get).orEmpty())
    }
    var selectedReasonId by remember(order) { mutableStateOf(order.reasonId.orEmpty()) }
    val selectedReasonLabel = reasonLabels[selectedReasonId].orEmpty()

    val canSave = orderDate != null

    val title =
        stringResource(
            if (isNew) Res.string.compras_extra_add_title else Res.string.compras_extra_edit_title,
        )
    AppModalCard(title = title, onDismiss = onDismiss) {
        Column(
            modifier = Modifier.fillMaxWidth().widthIn(max = 520.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            AppDatePickerField(
                value = orderDate,
                onValueChange = { orderDate = it },
                label = stringResource(Res.string.compras_extra_col_order_date),
                modifier = Modifier.fillMaxWidth(),
                bottomBorderOnly = true,
                borderColor = MaterialTheme.colorScheme.outline,
                horizontalPadding = 0.dp,
            )
            AppDatePickerField(
                value = deliveryDate,
                onValueChange = { deliveryDate = it },
                label = stringResource(Res.string.compras_extra_col_delivery_date),
                modifier = Modifier.fillMaxWidth(),
                optional = true,
                bottomBorderOnly = true,
                borderColor = MaterialTheme.colorScheme.outline,
                horizontalPadding = 0.dp,
            )
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = stringResource(Res.string.compras_extra_col_reason),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                SearchableSelect(
                    leadingIcon = null,
                    options = reasonOptions,
                    selected = selectedReasonLabel,
                    onSelect = { label ->
                        selectedReasonId =
                            if (label == noneReason) {
                                ""
                            } else {
                                reasonLabels.entries
                                    .firstOrNull { it.value == label }
                                    ?.key
                                    .orEmpty()
                            }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    placeholder = stringResource(Res.string.compras_extra_reason_placeholder),
                    searchPlaceholder = stringResource(Res.string.compras_extra_reason_search),
                    searchable = true,
                    underlined = true,
                    borderColor = MaterialTheme.colorScheme.outline,
                    horizontalPadding = 0.dp,
                )
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = stringResource(Res.string.compras_extra_col_unit),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                SearchableSelect(
                    leadingIcon = null,
                    options = options,
                    selected = selectedUnitLabel,
                    onSelect = { selectedUnitLabel = if (it == noneLabel) "" else it },
                    modifier = Modifier.fillMaxWidth(),
                    placeholder = unitPlaceholder,
                    searchPlaceholder = stringResource(Res.string.compras_extra_units_search),
                    searchable = true,
                    underlined = true,
                    borderColor = MaterialTheme.colorScheme.outline,
                    horizontalPadding = 0.dp,
                )
            }
            AppTextField(
                value = description,
                onValueChange = { description = it },
                label = stringResource(Res.string.compras_extra_col_description),
                singleLine = false,
                modifier = Modifier.fillMaxWidth(),
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.compras_action_cancel),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Outlined,
                )
                AppButton(
                    text = stringResource(Res.string.compras_action_save),
                    onClick = {
                        orderDate?.let { selectedOrderDate ->
                            val unitId = labels.entries.firstOrNull { it.value == selectedUnitLabel }?.key
                            onSave(
                                order.copy(
                                    orderDate = selectedOrderDate.toString(),
                                    deliveryDate = deliveryDate?.toString(),
                                    description = description.trim(),
                                    reasonId = selectedReasonId.ifBlank { null },
                                    reasonName = reasonLabels[selectedReasonId],
                                    reasonActive =
                                        availableReasons.firstOrNull { it.id == selectedReasonId }?.active == true,
                                    unitId = unitId,
                                    unitName = unitId?.let { id -> units.firstOrNull { it.id == id }?.name },
                                ),
                            )
                        }
                    },
                    enabled = canSave,
                )
            }
        }
    }
}

@Composable
fun PurchasesExtraDeleteDialog(
    order: PurchasesExtraOrder,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    AppModalCard(
        title = stringResource(Res.string.compras_extra_delete_title),
        onDismiss = onDismiss,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().widthIn(max = 440.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        ) {
            Text(
                text = stringResource(Res.string.compras_extra_delete_message),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            if (order.description.isNotBlank()) {
                Text(
                    text = order.description,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurface,
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.compras_action_cancel),
                    onClick = onDismiss,
                    variant = PortalButtonVariant.Outlined,
                )
                AppButton(
                    text = stringResource(Res.string.compras_action_delete),
                    onClick = onConfirm,
                )
            }
        }
    }
}

@Composable
private fun ExtraDetailRow(
    label: String,
    value: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}

private fun parseExtraDateInput(value: String?): LocalDate? {
    val trimmed = value?.trim().orEmpty()
    if (trimmed.isEmpty()) return null
    val parts = trimmed.split("/")
    if (parts.size == 3) {
        val day = parts[0].toIntOrNull()
        val month = parts[1].toIntOrNull()
        val year = parts[2].toIntOrNull()
        if (day != null && month != null && year != null) {
            return runCatching { LocalDate(year, month, day) }.getOrNull()
        }
    }
    return runCatching { LocalDate.parse(trimmed) }.getOrNull()
}

private fun List<PurchasesExtraUnit>.labels(): Map<String, String> {
    val repeated = groupingBy { it.name }.eachCount().filterValues { it > 1 }.keys
    return associate { unit ->
        unit.id to if (unit.name in repeated && unit.director != null) "${unit.name} — ${unit.director}" else unit.name
    }
}
